import React, { useState, useEffect, useRef } from 'react';
import VideoPlayer from './VideoPlayer';
import DocumentViewer from './DocumentViewer';
import QuizPlayer from './QuizPlayer';
import QuizResultsModal from './QuizResultsModal';
import { appendTokenToUrl } from '../../api/courseService';

interface ContentPlayerProps {
  contentType: 'video' | 'document' | 'quiz';
  contentUrl: string;
  title: string;
  description?: string;
  questions?: any[];
  onContentComplete?: () => void;
  userId?: string;
  courseId?: string;
  lessonId?: string;
  resumeTime?: number;
  userName?: string;
  onDurationChange?: (seconds: number) => void;
}

const ContentPlayer: React.FC<ContentPlayerProps> = ({
  contentType,
  contentUrl,
  title,
  description,
  questions = [],
  onContentComplete,
  userId,
  courseId,
  lessonId,
  resumeTime = 0,
  userName,
  onDurationChange
}) => {
  const [showQuizResults, setShowQuizResults] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [quizTotal, setQuizTotal] = useState(0);
  const [processedUrl, setProcessedUrl] = useState<string>(contentUrl);
  const hasMarkedComplete = useRef(false);
  
  // Process URL to add authentication token
  useEffect(() => {
    if (contentUrl) {
      const tokenizedUrl = appendTokenToUrl(contentUrl);
      setProcessedUrl(tokenizedUrl || contentUrl);
    }
  }, [contentUrl]);

  // Mark content as complete when it's viewed (for document and video)
  useEffect(() => {
    // Only mark complete for document and video content (not quiz)
    if (contentType !== 'quiz' && contentUrl && !hasMarkedComplete.current) {
      // Use a small delay to ensure content is actually viewed
      const timer = setTimeout(() => {
        // Remove auto-completion to prevent video restarts
        console.log('Auto-completion disabled to prevent video restarts');
      }, 3000); // 3 second delay before marking as complete
      
      return () => clearTimeout(timer);
    }
  }, [contentType, contentUrl]);

  const getMediaType = (url: string): 'image' | 'video' | 'unknown' => {
    if (!url) return 'unknown';
    
    // Check if it's a base64 encoded image
    if (url.startsWith('data:image/')) {
      return 'image';
    }
    
    // Check for common image extensions
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
    if (imageExtensions.some(ext => url.toLowerCase().includes(ext))) {
      return 'image';
    }
    
    // Check for common video extensions or patterns
    const videoExtensions = ['.mp4', '.m3u8', '.avi', '.mov', '.wmv', '.flv', '.webm'];
    const videoPatterns = ['/video/', '/stream/', 'hls', 'mpegurl'];
    
    if (videoExtensions.some(ext => url.toLowerCase().includes(ext)) ||
        videoPatterns.some(pattern => url.toLowerCase().includes(pattern))) {
      return 'video';
    }
    
    // If URL contains "image" assume it's an image
    if (url.toLowerCase().includes('image')) {
      return 'image';
    }
    
    // If URL contains "video" assume it's a video
    if (url.toLowerCase().includes('video')) {
      return 'video';
    }
    
    // Default to image for unknown cases (most thumbnails are images)
    return 'image';
  };

  const handleQuizComplete = (score: number, total: number) => {
    setQuizScore(score);
    setQuizTotal(total);
    setShowQuizResults(true);
    
    // Mark content as complete when quiz is completed
    if (onContentComplete && !hasMarkedComplete.current) {
      onContentComplete();
      hasMarkedComplete.current = true;
    }
  };

  const renderContent = () => {
    switch (contentType) {
      case 'video':
        const mediaType = getMediaType(processedUrl);
        if (mediaType === 'video') {
          return (
            <VideoPlayer
              src={processedUrl}
              autoPlay={true}
              showControls={true}
              className="w-full h-full"
              userId={userId}
              courseId={courseId}
              lessonId={lessonId}
              resumeTime={resumeTime}
              onProgressUpdate={onContentComplete}
              userName={userName}
              onDurationChange={onDurationChange}
            />
          );
        } else if (mediaType === 'image') {
          return (
            <div className="flex justify-center items-center h-full w-full bg-black">
              <img
                src={processedUrl}
                alt={title}
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "/placeholder.svg";
                }}
              />
            </div>
          );
        } else {
          return (
            <div className="w-full h-full flex items-center justify-center bg-black text-white">
              <p className="text-base font-medium">No media available</p>
            </div>
          );
        }
      
      case 'document':
        return (
          <DocumentViewer 
            url={processedUrl} 
            title={title} 
            description={description}
            userId={userId}
            courseId={courseId}
            lessonId={lessonId}
          />
        );
      
      case 'quiz':
        return (
          <>
            <QuizPlayer 
              questions={questions} 
              title={title} 
              description={description} 
              onQuizComplete={handleQuizComplete}
            />
            <QuizResultsModal
              isOpen={showQuizResults}
              onClose={() => setShowQuizResults(false)}
              onRetake={() => setShowQuizResults(false)}
              score={quizScore}
              total={quizTotal}
            />
          </>
        );
      
      default:
        return (
          <div className="w-full h-full flex items-center justify-center bg-black text-white">
            <p className="text-base font-medium">Unsupported content type</p>
          </div>
        );
    }
  };

  // const getContentLabel = () => {
  //   switch (contentType) {
  //     case 'video': return 'Video';
  //     case 'document': return 'Document';
  //     case 'quiz': return 'Quiz';
  //     default: return 'Lesson';
  //   }
  // };

  // const getContentColor = () => {
  //   switch (contentType) {
  //     case 'video': return 'bg-purple-600';
  //     case 'document': return 'bg-blue-600';
  //     case 'quiz': return 'bg-green-600';
  //     default: return 'bg-gray-600';
  //   }
  // };

  return (
    <div className="flex flex-col h-full w-full max-w-4xl mx-auto">
      <div className="flex-1 bg-white rounded-lg overflow-hidden shadow-sm">
        {renderContent()}
      </div>
    </div>
  );
};

export default ContentPlayer;